import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';

import { useAuth } from '../../../store/AuthStore';
import { devicesStore } from '../../../store/devices/DevicesStore';
import { ResourceUtils } from '../../../store/ResourceUtils';
import { zonesStore } from '../../../store/zones/ZonesStore.ts';
import { ZoneUtils } from '../../../store/zones/ZoneUtils.ts';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useRootZoneId, useZonesLoading } from '../../../store/zones/useZones.ts';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { IconButton } from '../../../components/buttons/IconButton';
import { ListBoxSection } from '../../../components/list-box/ListBoxSection';
import { Dialog } from '../../../components/overlay/Dialog';

import { ColumnItemCard } from './ColumnItemCard';
import { ColumnTypeApp } from './ColumnTypeApp';
import { ColumnTypeDevice } from './ColumnTypeDevice';
import { ColumnTypeManager } from './ColumnTypeManager';
import { ColumnTypeRoot } from './ColumnTypeRoot';
import { ColumnTypeZone } from './ColumnTypeZone';
import { ColumnItemApp } from './explorer/ColumnItemApp';
import { ColumnItemDevice } from './explorer/ColumnItemDevice';
import { ColumnItemGeneric } from './explorer/ColumnItemGeneric';
import { ColumnItemZone } from './explorer/ColumnItemZone';
import { ColumnView } from './explorer/ColumnView';
import { FlowCardExplorerDialogSearch } from './FlowCardExplorerDialogSearch';

FlowCardExplorerDialog.propTypes = {
  cardType: PropTypes.string.isRequired,
  advanced: PropTypes.bool,
  currentCard: PropTypes.object,
  onCardSelect: PropTypes.func,
  onCloseRequest: PropTypes.func,
};

export function FlowCardExplorerDialog({ advanced = true, ...props }) {
  const { i18n } = useI18n();
  const { experimentalFlag } = useAuth();
  const isLoading = useZonesLoading();
  const { rootZoneId } = useRootZoneId();

  function getDefaultColumns() {
    if (props.currentCard) {
      const ownerUri = ResourceUtils.getOwnerUri(props.currentCard);

      if (ResourceUtils.isManagerUri(ownerUri)) {
        return [
          {
            type: 'root',
            key: 'root',
          },
          {
            type: 'manager',
            key: ownerUri,
          },
        ];
      }

      if (ResourceUtils.isAppUri(ownerUri)) {
        return [
          {
            type: 'root',
            key: 'root',
          },
          {
            type: 'app',
            key: ownerUri,
          },
        ];
      }

      if (ResourceUtils.isDeviceUri(ownerUri)) {
        const deviceId = ownerUri.substring('homey:device:'.length);
        const state = devicesStore.get();

        // if the card was rendered we can assume
        // the device will be in the store
        const device = state.byId?.[deviceId];
        const driverOwnerUri = device?.driverId.split(':').slice(0, 3).join(':');

        return [
          {
            type: 'root',
            key: 'root',
          },
          ...(driverOwnerUri != null
            ? [
                {
                  type: 'app',
                  key: driverOwnerUri,
                },
              ]
            : []),
          {
            type: 'device',
            key: deviceId,
          },
        ];
      }

      if (ResourceUtils.isZoneUri(ownerUri)) {
        // this whole approach assumes the data is there
        // so if it's not there just return the default view
        const zoneId = ownerUri.substring('homey:zone:'.length);
        const state = zonesStore.get();

        if (state.byId?.[zoneId] != null) {
          const path = ZoneUtils.getPathForZone(state.byId, state.byId[zoneId]);

          return [
            {
              type: 'root',
              key: 'root',
            },
            ...path.map((zoneId) => {
              return {
                type: 'zone',
                key: zoneId,
              };
            }),
            {
              type: 'zone',
              key: zoneId,
            },
          ];
        }
      }
    }

    return [
      {
        type: 'root',
        key: 'root',
      },
      {
        type: 'zone',
        key: rootZoneId,
      },
    ];
  }

  function renderSection() {
    return <ListBoxSection />;
  }

  function renderItem({ item, value, state }) {
    switch (value.type) {
      case 'zone':
        return <ColumnItemZone zoneId={item.key} />;
      case 'app':
        return <ColumnItemApp appId={value.context.appId} />;
      case 'device':
        return <ColumnItemDevice deviceId={item.key} />;
      case 'mood':
        return <ColumnItemCard />;
      case 'card':
        return <ColumnItemCard />;
      default:
        return <ColumnItemGeneric />;
    }
  }

  function getColumnComponent({ type }) {
    let Component = React.Fragment;

    switch (type) {
      case 'root':
        Component = ColumnTypeRoot;
        break;
      case 'manager':
        Component = ColumnTypeManager;
        break;
      case 'app':
        Component = ColumnTypeApp;
        break;
      case 'zone':
        Component = ColumnTypeZone;
        break;
      case 'device':
        Component = ColumnTypeDevice;
        break;
      default:
        break;
    }

    return Component;
  }

  const ref = useRef({});
  ref.current.onCardSelect = props.onCardSelect;
  const onCardSelect = useCallback((cardKey, card, cardDroptoken) => {
    return ref.current.onCardSelect(cardKey, card, cardDroptoken);
  }, []);

  const cardFilter = useCallback(
    (card) => {
      if (card.deprecated === true) {
        return false;
      }

      if (
        experimentalFlag !== true &&
        ResourceUtils.getOwnerUri(card) === 'homey:manager:schedules'
      ) {
        return false;
      }

      if (advanced === false && card.advanced === true) {
        return false;
      }

      // Hide AdvancedFlow cards from normal Flows.
      if (advanced === false && props.cardType === 'action' && card.tokens != null) {
        return false;
      }

      // Hide programmatic_trigger from AdvancedFlow since we have the start card.
      if (
        advanced === true &&
        props.cardType === 'trigger' &&
        ResourceUtils.getOwnerUri(card) === 'homey:manager:flow' &&
        card.id === 'homey:manager:flow:programmatic_trigger'
      ) {
        return false;
      }

      return true;
    },
    [props.cardType, advanced, experimentalFlag],
  );

  return (
    <Dialog containFocus={true} autoFocus={true} onClose={props.onCloseRequest}>
      <FlowCardExplorerDialog.Root>
        <FlowCardExplorerDialog.Header>
          <h3>{`${i18n.messageFormatter(`flow.${props.cardType}`)}...`}</h3>

          <FlowCardExplorerDialogSearch
            advanced={advanced}
            cardType={props.cardType}
            cardFilter={cardFilter}
            onCardSelect={onCardSelect}
          />

          <IconButton
            excludeFromTabOrder={true}
            url={glyph.core.cross}
            color={theme.color.icon_light}
            hoverColor={theme.color.icon_light_hover}
            size={theme.icon.size_default}
            onPress={props.onCloseRequest}
          />
        </FlowCardExplorerDialog.Header>

        {isLoading === false && rootZoneId != null && (
          <ColumnView
            columnProps={{
              cardType: props.cardType,
              cardFilter,
              onCardSelect,
            }}
            getDefaultColumns={getDefaultColumns}
            getColumnComponent={getColumnComponent}
            renderItem={renderItem}
            renderSection={renderSection}
          />
        )}
      </FlowCardExplorerDialog.Root>
    </Dialog>
  );
}

FlowCardExplorerDialog.Root = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 1200px;
  height: min(1000px, 90vh);
`;

FlowCardExplorerDialog.Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: ${su(8)};
  height: ${su(8)};
  padding: ${su(1, 2)};
  background-color: ${theme.color.body};
  border-radius: ${theme.borderRadius.default} ${theme.borderRadius.default} 0 0;
  border-bottom: 1px solid ${theme.color.line};
`;
